import React, { useMemo } from 'react';
import { OwnerGroupRetentionCard } from '~/components/OwnerGroupRetentionCard/OwnerGroupRetentionCard';
import { OwnerGroupPortfolioStatsPanel } from '~/components/OwnerGroupPortfolioStatsPanel/OwnerGroupPortfolioStatsPanel';
import { useQuery } from 'react-query';
import { fetchOwnerGroupPortfolioStats, fetchOwnerGroupRetentionExpiry } from '~/api/analytics';
import { OwnerGroupPortfolioStats } from '~/types/analytics';
import styles from './OwnerGroupPortfolioSummary.module.css';

interface Props {
  ownerGroupId: string;
  ownerGroupName: string;
}

export const OwnerGroupPortfolioSummary = ({
  ownerGroupId,
  ownerGroupName
}: Props) => {
  const { data: portfolioStatsRes } = useQuery(
    ['ownerGroupPortfolioStats', ownerGroupId],
    async () => {
      try {
        const res = await fetchOwnerGroupPortfolioStats(ownerGroupId);
        return res.data;
      } catch (e: any) {
        if (e?.response?.status === 404) return null;
        throw e;
      }
    },
    { refetchOnWindowFocus: false, enabled: !!ownerGroupId }
  );

  const portfolioStats = useMemo<OwnerGroupPortfolioStats | null>(() => {
    if (!portfolioStatsRes) return null;
    return portfolioStatsRes as OwnerGroupPortfolioStats;
  }, [portfolioStatsRes]);

  const { data: retentionRes } = useQuery(
    ['ownerGroupRetentionExpiry', ownerGroupId],
    async () => {
      try {
        const res = await fetchOwnerGroupRetentionExpiry(ownerGroupId);
        return res.data;
      } catch (e: any) {
        if (e?.response?.status === 404) return null;
        throw e;
      }
    },
    { refetchOnWindowFocus: false, enabled: !!ownerGroupId }
  );

  const hasRetentionData = !!(retentionRes && (Array.isArray(retentionRes) ? retentionRes : [retentionRes]).some((r: any) => r?.month));

  const showRightPanel = !!portfolioStats && portfolioStats.buildingCount > 1;

  return (
    <div className={`${styles.summary} ${!hasRetentionData ? styles.noRetention : ''}`}>
      {hasRetentionData && (
        <div className={styles.left}>
          <OwnerGroupRetentionCard
            ownerGroupId={ownerGroupId}
            ownerGroupName={ownerGroupName}
          />
        </div>
      )}
      {showRightPanel && (
        <div className={styles.right}>
          <OwnerGroupPortfolioStatsPanel portfolioStats={portfolioStats} />
        </div>
      )}
    </div>
  );
};
